// Container-based iso renderer shared by FarmScene (interactive), VisitFarmScene (read-only)
// and the hall's door window. Ground tiles go in `ground` (drawn in creation order — never
// overlap); crops, markers, avatars and effects go in `objects`, which the host sorts by
// `depth` every frame (see iso.depthAt). The host moves/scales `container` (farmView.mjs).
//
// The ground is two layers: `lawn`, an endless tiled grass backdrop (one seamless TileSprite
// covering the farm plus a margin, so panning/zooming never reveals its edge), and `ground`,
// which now holds only the boundary outline plus one diamond Image per owned plot (fallow or
// plowed/planted/ready). A cell with no plot record shows the lawn straight through — there is
// no per-cell grass tile any more (real per-cell grass diamonds show seams; the rectangular
// lawn texture tiles cleanly instead).
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import {
  TILE_W, TILE_H, CROP_BASE_Y, LAWN_MARGIN, HORIZON_MARGIN,
  cellCentre, cellFToLocal, parseKey, farmExtent, farmOrigin, depthAt, pathCells, gardenGateX,
} from './iso.mjs';
import { podworkoState, podworkoSig, podworkoPathCells, podworkoSpriteRect, carSpot, carOrigin, FARM_CAR_SCALE } from './podworkoPlot.mjs';
import { PODWORKO_CAR_SPOTS } from './podworkoAnchors.mjs';
import { areaEdges } from './footprint.mjs';
import { lawnProps, seedFrom, VIEW_REACH, housePlacement, HOUSE } from './lawnProps.mjs';
import { localViewRect, spriteInView } from './viewCull.mjs';
import { anchorFor } from './sceneryAnchors.mjs';
import { plotStage, cropStageKey, cropArtId } from './farmRules.mjs';

export { cropStageKey };   // the hall's garden window draws the same stages
import { ensureFarmPlaceholders, TILE_KEYS, CROP_KEYS, CROP_TINT } from './farmPlaceholders.js';
import { preloader } from '../scenes/PreloadScene.js';
import { FarmObjectLayer } from './FarmObjectLayer.js';
import { FarmAnimalLayer } from './FarmAnimalLayer.js';
import { gameState } from '../state/gameState.js';
import { iconText } from '../ui/iconText.js';
import { frontRailY, frontPostY } from './fenceDepth.mjs';
import { serverNow } from '../state/serverClock.mjs';

const CROP_SIZE = { seedling: 20, growing: 32, ready: 44 };
const MARK_DEPTH = -1;   // hover/queued outlines: above ground, below everything in objects

const LAWN_KEY = 'farm_lawn';
const PLOT_KEYS = { fallow: 'farm_tile_fallow', plowed: 'farm_tile_plowed' };
const PATH_KEY = 'farm_tile_path';
const POST_KEY = 'farm_fence_post';
// The picket fence around the owned land: one rotated segment per cell edge. `se` slopes
// down-right on screen, `sw` down-left (scripts/farm_scenery.py along_edge).
const FENCE_KEYS = { se: 'farm_fence_se', sw: 'farm_fence_sw' };
// The gate: a panel-sized segment in the fence line, hinged at the diamond's left corner
// post and closing against a second post one cell edge along (scripts/farm_scenery.py).
const GATE_KEY = 'farm_gate';
const SCENERY_SCALE = 0.5;     // every scenery texture is authored at 2×
// A front-edge fence stands a touch in front of whatever grows on the row it fences in.
// …and is see-through, so the crops on the rows behind it show (owner 2026-10-27: "the two bottom
// fence rows, I'd like them to be slightly transparent"; then "wooden posts also, make it more transparent").
// Rails, gates and posts alike.
export const FRONT_FENCE_ALPHA = 0.4;

export { LAWN_MARGIN };

// Real art if it loaded, else the runtime placeholder (tests/scenes that skip the loader).
function lawnTexture(scene) { return scene.textures.exists(LAWN_KEY) ? LAWN_KEY : TILE_KEYS.grass; }
function plotTexture(scene, stage) {
  const real = stage === 'fallow' ? PLOT_KEYS.fallow : PLOT_KEYS.plowed;
  if (scene.textures.exists(real)) return real;
  return stage === 'fallow' ? TILE_KEYS.fallow : TILE_KEYS.plowed;
}


export class FarmWorld {
  // `owner`: whose farm this is (a player id) — seeds the scenery outside the fence, so it looks
  // the same to them and to visitors, and different on every other farm.
  constructor(scene, { getData, depth = 0, owner = null }) {
    ensureFarmPlaceholders(scene);
    this.scene = scene;
    this.getData = getData;
    this._scenerySeed = owner ? seedFrom(owner) : 1;
    this.container = scene.add.container(0, 0).setDepth(depth);
    // Endless grass backdrop, drawn first so everything else sits on top of it.
    this.lawn = scene.add.tileSprite(0, 0, 1, 1, lawnTexture(scene)).setOrigin(0, 0);
    this.lawn.setTileScale(0.5, 0.5);   // texture is authored at 2×
    this.ground = scene.add.container(0, 0);
    // All the scenery outside the fence: hundreds of props nobody walks among and none reaching over
    // the farm (lawnProps.test), so one layer under the farm's, sorted once when painted.
    this.forest = scene.add.container(0, 0);
    this.objects = scene.add.container(0, 0);
    this.objectLayer = new FarmObjectLayer(scene, this);
    this.animalLayer = new FarmAnimalLayer(scene, this);        // 4c: chickens and cows roam the grass
    // A visit's view has no catalog of its own: the visitor's catalog describes the same objects.
    this._objectData = () => { const d = this.getData() ?? {}; return d.catalog ? d : { ...d, catalog: gameState.raw?.catalog }; };
    this.container.add([this.lawn, this.ground, this.forest, this.objects]);
    this._tiles = new Map();
    this._crops = new Map();
    this._marks = new Map();
    this._boundary = null;
    this._scenery = [];        // fence, gate, posts, path tiles and lawn props
    this._destroyed = false;
    // Real crop keys we've already registered a whenReady() swap for — one registration per
    // key no matter how many plots/refreshes ask for it (the staged loader may still be
    // fetching farm_crop_* when this scene first draws; see _paintCrop).
    this._pendingCrop = new Set();
    this._hover = scene.add.image(0, 0, TILE_KEYS.hover).setDisplaySize(TILE_W, TILE_H).setVisible(false).setDepth(MARK_DEPTH);
    this.objects.add(this._hover);
    // Vehicle areas (FarmVille 1): yellow outline under the cursor, red dashed while waiting,
    // green fill while a vehicle works it. One Graphics, redrawn when any of them changes.
    this._areaGfx = scene.add.graphics().setDepth(MARK_DEPTH);
    this.objects.add(this._areaGfx);
    this._hoverArea = null;
    this._queuedAreas = [];
    this._hintText = iconText(scene, 0, 0, '', { fontFamily: 'system-ui', fontSize: '16px', color: '#fff', fontStyle: 'bold', backgroundColor: '#000000aa', padding: { x: 6, y: 3 } })
      .setOrigin(0.5, 1).setDepth(1e9).setVisible(false);
    this.objects.add(this._hintText);
    this._size = 0;
    this.refresh(serverNow());
  }

  get size() { return this._size; }
  get extent() { return farmExtent(this._size); }
  _farm() { return this.getData()?.farm ?? { size: 8, cells: {} }; }

  refresh(nowMs = serverNow()) {
    const farm = this._farm();
    // A grow, or Podwórko unlocked / upgraded / its garage cars changed: repaint the ground.
    const podSig = podworkoSig(podworkoState(this.getData()));
    if (farm.size !== this._size || podSig !== this._podSig) { this._rebuildGround(farm.size); this._drawAreas?.(); }
    const seen = new Set();
    for (const [key, plot] of Object.entries(farm.cells ?? {})) {
      const { x, y } = parseKey(key);
      if (x >= farm.size || y >= farm.size) continue;
      seen.add(key);
      const stage = plotStage(plot, nowMs);
      const tex = plotTexture(this.scene, stage);
      let img = this._tiles.get(key);
      if (!img) {
        const c = cellCentre(x, y, farm.size);
        img = this.scene.add.image(c.x, c.y, tex);
        this.ground.add(img);
        this._tiles.set(key, img);
      } else if (img.texture.key !== tex) {
        img.setTexture(tex);
      }
      img.setDisplaySize(TILE_W, TILE_H);
      if (stage === 'growing' || stage === 'ready') this._paintCrop(key, x, y, plot, stage, nowMs);
      else this._removeCrop(key);
    }
    // A key no longer in farm.cells went back to grass — the lawn already shows through.
    for (const [key, tile] of this._tiles) {
      if (!seen.has(key)) { tile.destroy(); this._tiles.delete(key); }
    }
    for (const key of [...this._crops.keys()]) if (!seen.has(key)) this._removeCrop(key);
    this.objectLayer.refresh(this._objectData(), nowMs);
    this.animalLayer.refresh(this._objectData());
  }

  // Per frame (the scenes' update): animals walk between refreshes.
  update(dtMs) { this.animalLayer.update(dtMs); this._cullStill(); }

  // Hide the still-layer props that are off screen (farm/viewCull.mjs) — recomputed only when the
  // pan / zoom changed; Phaser skips invisible sprites. The house (origin top-left) is never culled.
  _cullStill() {
    const c = this.container;
    const key = `${Math.round(c.x)},${Math.round(c.y)},${c.scaleX.toFixed(3)},${this.forest.list.length}`;
    if (key === this._cullKey) return;
    this._cullKey = key;
    const r = localViewRect({ x: c.x, y: c.y, scale: c.scaleX }, { w: GAME_WIDTH, h: GAME_HEIGHT }, 120);
    for (const img of this.forest.list) {
      if (img._noCull) continue;
      img.setVisible(spriteInView({ x: img.x, y: img.y, w: img.displayWidth, h: img.displayHeight }, r));
    }
  }

  _rebuildGround(size) {
    this._size = size;
    for (const t of this._tiles.values()) t.destroy();
    for (const c of this._crops.values()) c.destroy();
    for (const m of this._marks.values()) m.destroy();
    this._tiles.clear(); this._crops.clear(); this._marks.clear();
    for (const s of this._scenery) s.destroy();
    this._scenery = [];
    this._podImgs = [];
    this._podSig = podworkoSig(podworkoState(this.getData()));

    const extent = farmExtent(size);
    this.lawn.setTexture(lawnTexture(this.scene));
    // The lawn's TOP edge is the horizon the backdrop stands on (farm/FarmBackdrop.js), so it
    // stops HORIZON_MARGIN cells above the farm — near enough to be on screen — while the
    // other three sides keep the full margin nobody is meant to reach.
    // …and on the other three sides reaches as far as the camera can ever show (lawnProps.VIEW_REACH).
    const rx = Math.max(LAWN_MARGIN * TILE_W, VIEW_REACH.x + 200), ry = Math.max(LAWN_MARGIN * TILE_H, VIEW_REACH.y + 200);
    this.lawn.setPosition(-rx, -HORIZON_MARGIN * TILE_H);
    this.lawn.setSize(extent.w + 2 * rx, extent.h + HORIZON_MARGIN * TILE_H + ry);
    this.lawn.setTileScale(0.5, 0.5);

    this._boundary?.destroy();
    this._boundary = null;
    this._paintPath(size);
    this._paintProps(size);
    this._paintHouse(size);
    this._paintPodworko(size);
    if (this._paintFence(size)) return;   // the real fence replaces the placeholder outline
    const ox = farmOrigin(size).x;
    const g = this.scene.add.graphics();
    g.lineStyle(3, 0xffffff, 0.35);
    g.beginPath();
    g.moveTo(ox, 0);
    g.lineTo(ox + extent.w / 2, extent.h / 2);
    g.lineTo(ox, extent.h);
    g.lineTo(ox - extent.w / 2, extent.h / 2);
    g.closePath();
    g.strokePath();
    const inset = 6;   // a second, thinner stroke just inside the first
    g.lineStyle(1, 0x2d602d, 0.5);   // a darker shade of the lawn, so the edge reads as grass
    g.beginPath();
    g.moveTo(ox, inset);
    g.lineTo(ox + extent.w / 2 - inset, extent.h / 2);
    g.lineTo(ox, extent.h - inset);
    g.lineTo(ox - extent.w / 2 + inset, extent.h / 2);
    g.closePath();
    g.strokePath();
    this.ground.add(g);
    this._boundary = g;
  }

  // A sprite drawn at half its authored size, pinned by `anchor`, remembered for teardown.
  _scenerySprite(key, x, y, anchor) {
    const img = this.scene.add.image(x, y, key).setOrigin(anchor[0], anchor[1]);
    const src = img.texture.getSourceImage();
    img.setDisplaySize(src.width * SCENERY_SCALE, src.height * SCENERY_SCALE);
    this._scenery.push(img);
    return img;
  }

  // The dirt path leading away from the gate: plain diamonds in `ground`, on cells outside
  // the farm (cellCentre is happy with negative/oversized coordinates).
  _paintPath(size) {
    if (!this.scene.textures.exists(PATH_KEY)) return;
    for (const cell of pathCells(size)) {
      const c = cellCentre(cell.x, cell.y, size);
      const img = this.scene.add.image(c.x, c.y, PATH_KEY).setDisplaySize(TILE_W, TILE_H);
      this.ground.add(img);
      this._scenery.push(img);
    }
  }

  // The farm owner's house at the end of the path, as it looks at their house level (the main
  // screen's house, farm-style). It sits in the still layer: props that would cover it are left out.
  _paintHouse(size) {
    const level = Math.min(5, Math.max(1, Math.floor(this.getData()?.house?.level ?? 1)));
    const key = `farm_house_lvl${level}`;
    this._houseLevel = level;
    if (!this.scene.textures.exists(key)) {
      preloader.whenReady?.(key, () => { if (!this._destroyed && this._size === size) this._paintHouse(size); });
      return;
    }
    const hp = housePlacement(size, level);
    const img = this.scene.add.image(hp.rect.x0, hp.rect.y0, key).setOrigin(0, 0);
    img._noCull = true;
    const src = img.texture.getSourceImage();
    img.setDisplaySize(HOUSE.w, (HOUSE.w * src.height) / src.width);   // the art's own aspect: never stretched
    img.setDepth(depthAt(hp.rect.y1));
    this._scenery.push(img);
    this.forest.add(img);
    this.forest.sort('depth');
  }

  // Podwórko beside the house (spec 2026-10-22): the path from its gate, its level's picture and the
  // cars in its garage. Nothing while it is locked. Sits in the still layer like the house: nobody walks
  // there, and lawnProps leaves the plot clear. Safe to call again (a texture landing late): it first
  // removes what it drew before.
  _paintPodworko(size) {
    for (const img of this._podImgs ?? []) { img.destroy(); this._scenery = this._scenery.filter((o) => o !== img); }
    this._podImgs = [];
    const st = podworkoState(this.getData());
    if (!st) return;
    const keep = (img) => { this._podImgs.push(img); this._scenery.push(img); return img; };
    if (this.scene.textures.exists(PATH_KEY)) {
      for (const cell of podworkoPathCells(size)) {
        const c = cellCentre(cell.x, cell.y, size);
        this.ground.add(keep(this.scene.add.image(c.x, c.y, PATH_KEY).setDisplaySize(TILE_W, TILE_H)));
      }
    }
    const waitFor = (k) => preloader.whenReady?.(k, () => { if (!this._destroyed && this._size === size) this._paintPodworko(size); });
    const key = `farm_podworko_lvl${st.level}`;
    const r = podworkoSpriteRect(size);
    if (this.scene.textures.exists(key)) {
      const img = keep(this.scene.add.image(r.x0, r.y0, key).setOrigin(0, 0).setDisplaySize(r.w, r.h).setDepth(depthAt(r.depthY)));
      img._noCull = true;
      this.forest.add(img);
    } else waitFor(key);
    st.cars.forEach((model, i) => {
      const spot = carSpot(st.level, i, size, PODWORKO_CAR_SPOTS);
      const carKey = `farm_car_${model}`;
      if (!spot) return;                                 // more cars than spots: the rest are not drawn
      if (!this.scene.textures.exists(carKey)) { waitFor(carKey); return; }
      const car = keep(this.scene.add.image(spot.x, spot.y, carKey));
      const src = car.texture.getSourceImage();
      car.setOrigin(...carOrigin(src.width, src.height));                  // on its pad by the footprint's middle
      car.setDisplaySize(src.width * FARM_CAR_SCALE, src.height * FARM_CAR_SCALE).setDepth(depthAt(r.depthY) + 1 + i);   // models keep their sizes
      car._noCull = true;
      this.forest.add(car);
    });
    this.forest.sort('depth');
    this._cullKey = null;
  }

  _paintProps(size) {
    for (const img of this._propImgs ?? []) { img.destroy(); this._scenery = this._scenery.filter((o) => o !== img); }
    this._propImgs = [];
    this._pendingProps ??= new Set();
    for (const p of lawnProps(size, this._scenerySeed, { podworko: !!podworkoState(this.getData()) })) {
      const key = `farm_prop_${p.kind}`;
      if (!this.scene.textures.exists(key)) {
        // The forest kinds load in tier 1 (assetManifest FARM_LATER_SCENERY): repaint once they land.
        if (!this._pendingProps.has(key)) { this._pendingProps.add(key); preloader.whenReady?.(key, () => this._repaintPropsSoon()); }
        continue;
      }
      const img = this._scenerySprite(key, p.x, p.y, [0.5, 1]).setDepth(depthAt(p.y)).setFlipX(p.flip);
      img.setDisplaySize(img.displayWidth * p.scale, img.displayHeight * p.scale);   // mirrored, ±10%: fewer lookalikes
      this.forest.add(img);
      this._propImgs.push(img);
    }
    this.forest.sort('depth');
    this._cullKey = null;
  }

  // Several prop kinds land within moments of each other: one repaint for the lot.
  _repaintPropsSoon() {
    if (this._propRepaint || this._destroyed) return;
    this._propRepaint = this.scene.time.delayedCall(250, () => {
      this._propRepaint = null;
      if (!this._destroyed && this._size) this._paintProps(this._size);
    });
  }

  // One segment per cell edge around the diamond, plus a post at each corner and the gate on
  // the edge the home exit opens through. Returns false (and draws nothing) when the art has
  // not landed, so _rebuildGround can keep the old outline as the fallback.
  _paintFence(size) {
    const has = (k) => this.scene.textures.exists(k);
    if (!has(FENCE_KEYS.se) || !has(FENCE_KEYS.sw)) return false;
    const o = farmOrigin(size);
    const e = farmExtent(size);
    // The diamond's four corners, clockwise from the top.
    const top = { x: o.x, y: 0 };
    const right = { x: o.x + e.w / 2, y: e.h / 2 };
    const bottom = { x: o.x, y: e.h };
    const left = { x: o.x - e.w / 2, y: e.h / 2 };
    // The two back edges (top→right, top→left) may hide behind the crops; the two front ones
    // (right→bottom, left→bottom) must stand in front of them, so they y-sort in `objects`.
    // The gates sit on the left→bottom edge: the house gate on cell (0, size-1) — the cell you walk off
    // to go home (iso.gardenExit) — and, while Podwórko is unlocked, its gate on cell (gardenGateX(size), size-1).
    const gates = podworkoState(this.getData()) ? [0, gardenGateX(size)] : [0];
    const edges = [
      { from: top, to: right, dir: 'se', front: false },
      { from: top, to: left, dir: 'sw', front: false },
      { from: right, to: bottom, dir: 'sw', front: true },
      { from: left, to: bottom, dir: 'se', front: true, gates },
    ];
    // The top corner is the far end of both back runs: its post goes down FIRST, so the two
    // runs meeting at it are drawn over it and it reads as standing behind the fence.
    if (has(POST_KEY)) this.ground.add(this._scenerySprite(POST_KEY, top.x, top.y, [0.5, 1]));
    for (const edge of edges) {
      for (let i = 0; i < size; i++) {
        const t = (i + 0.5) / size;
        const mx = edge.from.x + (edge.to.x - edge.from.x) * t;
        const my = edge.from.y + (edge.to.y - edge.from.y) * t;
        const key = (edge.gates?.includes(i) && has(GATE_KEY)) ? GATE_KEY : FENCE_KEYS[edge.dir];
        const img = this._scenerySprite(key, mx, my, anchorFor(key));
        if (edge.front) { img.setDepth(depthAt(frontRailY(my))).setAlpha(FRONT_FENCE_ALPHA); this.objects.add(img); }
        else this.ground.add(img);
      }
    }
    // Each gate hangs between two posts of its own (the diamond's corners already have theirs).
    if (has(POST_KEY)) {
      for (const g of gates) {
        for (const k of [g, g + 1]) {
          if (k === 0 || k === size) continue;
          const p = { x: left.x + ((bottom.x - left.x) * k) / size, y: left.y + ((bottom.y - left.y) * k) / size };
          const post = this._scenerySprite(POST_KEY, p.x, p.y, [0.5, 1]);
          post.setDepth(depthAt(frontPostY(p.y) + 1)).setAlpha(FRONT_FENCE_ALPHA);
          this.objects.add(post);
        }
      }
    }
    if (has(POST_KEY)) {
      for (const [corner, front] of [[right, true], [bottom, true], [left, true]]) {
        const img = this._scenerySprite(POST_KEY, corner.x, corner.y, [0.5, 1]);
        if (front) { img.setDepth(depthAt(frontPostY(corner.y))).setAlpha(FRONT_FENCE_ALPHA); this.objects.add(img); }
        else this.ground.add(img);
      }
    }
    return true;
  }

  _paintCrop(key, x, y, plot, stage, nowMs) {
    const sk = cropStageKey(plot, stage, nowMs);
    const c = cellCentre(x, y, this._size);
    // Real art if it loaded (authored 2×, a cluster of plants meant to sit on the plowed
    // diamond with its bottom just past the tile centre), else the tinted placeholder circle
    // — an unknown/not-yet-downloaded crop still renders something.
    // A flower colour (Kwiaty §7) not downloaded yet shows the plain flower meanwhile.
    const colourKey = `farm_crop_${cropArtId(plot.seed, plot.c)}_${sk}`;
    const realKey = this.scene.textures.exists(colourKey) ? colourKey : `farm_crop_${plot.seed}_${sk}`;
    const useReal = this.scene.textures.exists(realKey);
    const tex = useReal ? realKey : CROP_KEYS[sk];

    let img = this._crops.get(key);
    const isNew = !img;
    if (isNew) {
      img = this.scene.add.image(c.x, c.y, tex);
      this.objects.add(img);
      this._crops.set(key, img);
    }
    // A freshly constructed image already reports `texture.key === tex` (the constructor set
    // it), so `isNew` has to count as dirty too, or a crop's very first paint keeps Phaser's
    // native (unscaled) frame size instead of ours.
    if (isNew || img.texture.key !== tex) {
      const wasReal = img.getData('real') === true;
      if (!isNew) img.setTexture(tex);
      img.setData('real', useReal);
      // Origin/size only need resetting when the crop switches between real art and the
      // placeholder circle (or on first paint) — real art anchors bottom-centre on the tile,
      // the placeholder is centred on it.
      if (useReal !== wasReal) img.setOrigin(0.5, useReal ? 1 : 0.5);
      if (useReal) {
        const src = img.texture.getSourceImage();
        img.setDisplaySize(src.width * 0.5, src.height * 0.5);
      }
    }
    if (useReal) {
      img.setPosition(c.x, c.y + CROP_BASE_Y).clearTint();
    } else {
      const size = CROP_SIZE[sk];
      img.setPosition(c.x, c.y - 6).setDisplaySize(size, size).setTint(CROP_TINT[plot.seed] ?? 0xffffff);
      // The staged loader may still be fetching this crop's art; swap it in the moment it
      // lands instead of waiting for the next unrelated refresh().
      if (!this._pendingCrop.has(realKey)) {
        this._pendingCrop.add(realKey);
        preloader.whenReady(realKey, () => { if (!this._destroyed) this.refresh(serverNow()); });
      }
    }
    img.setDepth(depthAt(c.y + TILE_H / 2));
  }

  _removeCrop(key) {
    const img = this._crops.get(key);
    if (img) { img.destroy(); this._crops.delete(key); }
  }

  setHover(cell) {
    if (!cell) { this._hover.setVisible(false); return; }
    const c = cellCentre(cell.x, cell.y, this._size);
    this._hover.setPosition(c.x, c.y).setVisible(true);
  }

  // `fill`: the fields in the block the click would actually work (the Konewka's: the ones it would water).
  setAreaHover(cells, hint = null, fill = null) {
    this._hoverArea = cells;
    this._hoverFill = cells ? fill : null;
    if (cells && hint) {
      const top = cells[0];
      const p = cellFToLocal(top.x, top.y, this._size);
      this._hintText.setText(hint).setPosition(p.x, p.y - 6).setVisible(true);
    } else this._hintText.setVisible(false);
    this._drawAreas();
  }

  // A one-cell hint (4b: "owoce za 1d 4h" over an unripe tree), in the vehicle-area hint's box.
  setHint(cell, text) {
    if (this._hoverArea) return;                      // a vehicle area's hint owns the box
    if (cell && text) {
      const p = cellFToLocal(cell.x, cell.y, this._size);
      this._hintText.setText(text).setPosition(p.x, p.y - 6).setVisible(true);
    } else this._hintText.setVisible(false);
  }

  setQueuedAreas(areas) { this._queuedAreas = areas; this._drawAreas(); }

  _drawAreas() {
    const g = this._areaGfx;
    if (!g) return;
    g.clear();
    const corner = (x, y) => cellFToLocal(x, y, this._size);
    const line = (edges, colour, width, dashed) => {
      g.lineStyle(width, colour, 1);
      for (const [x0, y0, x1, y1] of edges) {
        const a = corner(x0, y0), b = corner(x1, y1);
        if (!dashed) { g.lineBetween(a.x, a.y, b.x, b.y); continue; }
        const steps = 6;                              // 3 dashes per cell edge
        for (let i = 0; i < steps; i += 2) {
          const t0 = i / steps, t1 = (i + 1) / steps;
          g.lineBetween(a.x + (b.x - a.x) * t0, a.y + (b.y - a.y) * t0, a.x + (b.x - a.x) * t1, a.y + (b.y - a.y) * t1);
        }
      }
    };
    for (const { cells, working } of this._queuedAreas) {
      if (working) {
        g.fillStyle(0x7ed957, 0.35);
        for (const c of cells) g.fillPoints([corner(c.x, c.y), corner(c.x + 1, c.y), corner(c.x + 1, c.y + 1), corner(c.x, c.y + 1)], true);
        line(areaEdges(cells), 0x4caf50, 2, false);
      } else line(areaEdges(cells), 0xe63946, 3, true);
    }
    if (this._hoverFill?.length) {
      g.fillStyle(0x4aa3ff, 0.4);                     // the water's blue (farmFxArt drawDrop)
      for (const c of this._hoverFill) g.fillPoints([corner(c.x, c.y), corner(c.x + 1, c.y), corner(c.x + 1, c.y + 1), corner(c.x, c.y + 1)], true);
    }
    if (this._hoverArea) line(areaEdges(this._hoverArea), 0xffd166, 3, false);
  }

  setQueued(keys) {
    for (const [key, img] of this._marks) if (!keys.has(key)) { img.destroy(); this._marks.delete(key); }
    for (const key of keys) {
      if (this._marks.has(key)) continue;
      const { x, y } = parseKey(key);
      const c = cellCentre(x, y, this._size);
      const img = this.scene.add.image(c.x, c.y, TILE_KEYS.queued).setDisplaySize(TILE_W, TILE_H).setDepth(MARK_DEPTH);
      this.objects.add(img);
      this._marks.set(key, img);
    }
  }

  sortObjects() { this.objects.sort('depth'); }

  destroy() {
    this._destroyed = true;
    this.objectLayer.destroy();
    for (const s of this._scenery) s.destroy();
    this._scenery = [];
    this.container.destroy(true);
  }
}
